/*
  Scroll to top.

  A custom element rather than a bare script, so the control owns its own
  listener and tears it down if it is ever removed from the document. It wraps
  the button instead of extending it: customised built-ins (`is="..."`) are not
  supported in Safari, which is the same reason the promo popup binds to a data
  attribute rather than upgrading its own tag.

  Lives in layout/theme.liquid, not in a section. It is chrome that belongs to
  every page rather than content a merchant places, so there is nothing here to
  configure and nothing to reorder.
*/
(function () {
  if (customElements.get('scroll-top-button')) return;

  class ScrollTopButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.visible = false;

      /*
        No requestAnimationFrame throttle. The handler reads one property and
        compares it to a boolean; the class write happens only on the two
        frames where the state actually flips, not on every scroll event. A
        rAF gate would add a scheduling hop to save nothing, and it would make
        the control dependent on frames being served - which they are not in a
        backgrounded tab, the same trap the promo popup's entrance fell into.
      */
      this.onScroll = () => this.update();
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.onClick = (event) => this.scrollToTop(event);
      this.button.addEventListener('click', this.onClick);

      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.button?.removeEventListener('click', this.onClick);
    }

    /*
      One viewport of scroll is the threshold. Measured against innerHeight
      rather than a fixed pixel count so the control appears at the same point
      in the reading experience on a phone and on a desktop, instead of arriving
      almost immediately on one and late on the other.

      Public because the appearance is driven by real scrolling, which a test
      harness cannot produce. Calling update() after setting scrollY exercises
      exactly the code path a scroll event runs.
    */
    update() {
      const shouldShow = window.scrollY > window.innerHeight;
      if (shouldShow === this.visible) return;

      this.visible = shouldShow;
      this.classList.toggle('scroll-top--visible', shouldShow);
    }

    scrollToTop(event) {
      event.preventDefault();

      /*
        Read at click time, not at connect time. Someone can change the system
        setting while the page is open, and the next click should honour it.
      */
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });

      /*
        Focus has to move, and not just for tidiness. The button hides itself
        once the page reaches the top, and leaving focus on a control that
        becomes visibility:hidden strands a keyboard user on nothing - the next
        Tab resumes from an element that is no longer there.

        The skip link is the right destination rather than a made-up tabindex
        on <body>: it is already the first thing in the tab order and already
        built to take focus, so a keyboard user lands where the page visually
        just went.
      */
      const skipLink = document.querySelector('.skip-to-content-link');
      if (skipLink) skipLink.focus({ preventScroll: true });
    }
  }

  customElements.define('scroll-top-button', ScrollTopButton);
})();
